import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { FirestoreAppDataProvider } from '../context/FirestoreAppDataProvider';
import { useUser } from '../context/UserContext';
import { useFirebaseAuthReady } from '../hooks/useFirebaseAuthReady';
import { AppLayout } from './AppLayout';
import { AppLoadingScreen } from './AppLoadingScreen';
import { ViewFadeIn } from './ViewFadeIn';
import { FinancialsView } from '../views/FinancialsView';
import { InvestmentCompanyDetailView } from '../views/InvestmentCompanyDetailView';
import { InvestmentCaseDetailView } from '../views/InvestmentCaseDetailView';
import { InvestmentCasesView } from '../views/InvestmentCasesView';
import { BankAccountDetailView } from '../views/BankAccountDetailView';
import { BankAccountsView } from '../views/BankAccountsView';
import { RealEstateDetailView } from '../views/RealEstateDetailView';
import { RealEstateView } from '../views/RealEstateView';
import { HomeView } from '../views/HomeView';
import { IntelligenceView } from '../views/IntelligenceView';
import { InvestmentsView } from '../views/InvestmentsView';
import { ScenariosView } from '../views/ScenariosView';
import { SettingsView } from '../views/SettingsView';
import { BusinessOpportunitiesView } from '../views/BusinessOpportunitiesView';
import { BusinessOpportunityDetailView } from '../views/BusinessOpportunityDetailView';
import { EntitiesView } from '../views/EntitiesView';
import { TasksView } from '../views/TasksView';
import { SkillsView } from '../views/SkillsView';

const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const SignupPage = lazy(() => import('../pages/auth/SignupPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const ConfirmPasswordPage = lazy(() => import('../pages/auth/ConfirmPasswordPage'));
const FounderUpdateAnalyzerPage = lazy(() => import('../pages/public/FounderUpdateAnalyzerPage'));

function LoadingFallback({ variant = 'route' }: { variant?: 'auth' | 'route' }) {
  return <AppLoadingScreen variant={variant} />;
}

export function AuthGate() {
  const { user, loading } = useUser();
  const isAuthReady = useFirebaseAuthReady();

  if (!isAuthReady || loading) {
    return <LoadingFallback variant="auth" />;
  }

  if (!user) {
    return (
      <Suspense fallback={<LoadingFallback variant="auth" />}>
        <ViewFadeIn>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-password" element={<ConfirmPasswordPage />} />
            <Route path="/founder-update-analysis" element={<FounderUpdateAnalyzerPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </ViewFadeIn>
      </Suspense>
    );
  }

  return (
    <FirestoreAppDataProvider userId={user.uid}>
      <Suspense fallback={<LoadingFallback variant="route" />}>
        <Routes>
          <Route path="/founder-update-analysis" element={<FounderUpdateAnalyzerPage />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomeView />} />
            <Route path="/home" element={<HomeView />} />
            <Route path="/assets" element={<FinancialsView />} />
            <Route path="/financials" element={<Navigate to="/assets" replace />} />
            <Route path="/bank-accounts/:accountId" element={<BankAccountDetailView />} />
            <Route path="/bank-accounts" element={<BankAccountsView />} />
            <Route path="/real-estate/:propertyId" element={<RealEstateDetailView />} />
            <Route path="/real-estate" element={<RealEstateView />} />
            <Route path="/entities" element={<EntitiesView />} />
            <Route path="/investment-cases" element={<InvestmentCasesView />} />
            <Route path="/investment-cases/:caseId" element={<InvestmentCaseDetailView />} />
            <Route path="/opportunities" element={<BusinessOpportunitiesView />} />
            <Route path="/opportunities/:opportunityId" element={<BusinessOpportunityDetailView />} />
            <Route path="/investments" element={<InvestmentsView />} />
            <Route path="/investments/:companyId" element={<InvestmentCompanyDetailView />} />
            <Route path="/intelligence" element={<IntelligenceView />} />
            <Route path="/scenarios" element={<ScenariosView />} />
            <Route path="/tasks" element={<TasksView />} />
            <Route path="/skills" element={<SkillsView />} />
            <Route path="/settings" element={<SettingsView />} />
          </Route>
          <Route path="*" element={<Navigate to="/investments" replace />} />
        </Routes>
      </Suspense>
    </FirestoreAppDataProvider>
  );
}
